import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import { Button, IconButton, TextField } from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import SideNav from "component/layouts/sideNav";

import {
  ADD,
  DAILY_WORK_ENTRY,
  DELETE,
  GO,
  LABOUR_MANAGEMENT,
} from "resources/constant/strings/string";
import {
  LIGHT_GREY_BACKGROUND,
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";
import {
  PATH_DAILY_WORK_ENTRY,
  PATH_DAILY_WORK_ENTRY_DETAILS,
  PATH_LOGIN,
} from "resources/constant/url/url";
import {
  setDates as setDweDates,
  setFilterModel as setDweFilterModel,
  setSortModel as setDweSortModel,
} from "store/Slice/dailyWorkEntrySlice";

import { APILABOURMGMT } from "apihandler/labourmgmt";
import { APILOGIN } from "apihandler/login";
import { APIMASTER } from "apihandler/master";

import {
  getWorkOrders,
  setItemList,
  setItems,
  setLabourerTypes,
  setProjectPhases,
  setSites,
  setSupplierCategories,
  setSuppliers,
  setUoms,
  setUsers,
  setWorkOrders,
  siteCellRenderer,
  supplierCellRenderer,
  userCellRenderer,
} from "component/cellRenderers/renderers";

import {
  setAlertConfirmDeletion,
  setAlertTitleText,
} from "component/modal/alertModal";

export default function DailyWorkEntry() {
  let { date } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { fromDate, toDate, filterModel, sortModel } = useSelector(
    (s) => s.dweList
  );

  const [dateValue, setDateValue] = useState(
    date ? dayjs(date) : fromDate ? dayjs(fromDate) : dayjs().startOf("month")
  );
  const [toDateValue, setToDateValue] = useState(
    toDate ? dayjs(toDate) : dayjs().endOf("month")
  );

  const gridRef = useRef();

  useEffect(() => {
    if (!APILOGIN.GetIsLoggedIn()) navigate(PATH_LOGIN);
  }, [navigate]);

  useEffect(() => {
    dispatch(
      setDweDates({
        fromDate: dateValue ? dateValue.format("YYYY-MM-DD") : null,
        toDate: toDateValue ? toDateValue.format("YYYY-MM-DD") : null,
      })
    );
  }, [dateValue, toDateValue, dispatch]);

  const [selectedRowId, setSelectedRowId] = useState(0);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const safeFrom = (dateValue ?? dayjs().startOf("month")).format("YYYY-MM-DD");

  const deleteModel = () => {
    if (selectedRowIds.length === 0) return;

    setAlertConfirmDeletion(
      "Confirmation needed",
      `Do you confirm on proceeding to delete ${selectedRowIds.length} record(s)?`
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      APILABOURMGMT.DeleteDailyWorkEntries(selectedRowIds)
        .then((resp) => resp.json())
        .then((data) => {
          if (data.statusCode === 204) {
            const selectedData = gridRef.current?.api?.getSelectedRows() ?? [];
            gridRef.current?.api?.applyTransaction({ remove: selectedData });
            setAlertTitleText("Success", "The record was deleted successfully");
          } else {
            setAlertTitleText(
              "Oops...",
              `Something went wrong...${data.message}`
            );
          }
        })
        .catch(() => setAlertTitleText("Network Error", "Please try again."));
    });
  };

  const onRowSelectionChanged = (event) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows.length > 0) {
      setSelectedRowId(selectedRows[0].id);
      setSelectedRowIds(selectedRows.map((row) => row.id));
    } else {
      setSelectedRowId(0);
      setSelectedRowIds([]);
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        flex: 0.5,
        headerName: "Entry Date",
        field: "entryDate",
        checkboxSelection: false,
        filter: false,
        floatingFilter: false,
        cellStyle: { "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Reference No",
        field: "referenceNo",
        cellStyle: { "border-right-color": "#e2e2e2" },
        wrapText: true,
        autoHeight: true,
      },
      {
        headerName: "Contractor Name",
        field: "contractorId",
        autoHeight: true,
        wrapText: true,
        valueGetter: (p) =>
          supplierCellRenderer({ value: p.data.contractorId }),
        cellStyle: { "border-right-color": "#e2e2e2" },
      },
      {
        headerName: "Site",
        field: "locationId",
        autoHeight: true,
        wrapText: true,
        valueGetter: (p) => siteCellRenderer({ value: p.data.locationId }),
        cellStyle: { "border-right-color": "#e2e2e2" },
      },
      {
        headerName: "Modified By",
        field: "modifiedBy",
        valueGetter: (p) => userCellRenderer({ value: p.data.modifiedBy }),
        cellStyle: { "border-right-color": "#e2e2e2" },
        autoHeight: true,
        wrapText: true,
      },
      {
        flex: 0.3,
        headerName: "Details",
        field: "id",
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params) => (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {params.data?.billId === 0 && (
              <IconButton
                aria-label="edit"
                onClick={() => {
                  navigate(PATH_DAILY_WORK_ENTRY_DETAILS + params.data.id, {
                    state: { date: safeFrom, isEdit: true },
                  });
                }}
              >
                <EditIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
              </IconButton>
            )}
            <IconButton
              aria-label="view"
              onClick={() => {
                navigate(PATH_DAILY_WORK_ENTRY_DETAILS + params.data.id, {
                  state: { date: safeFrom, isEdit: false },
                });
              }}
            >
              <VisibilityIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
            </IconButton>
          </div>
        ),
      },
    ],
    [navigate, safeFrom]
  );

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      editable: false,
      flex: 1,
      filter: "agTextColumnFilter",
      filterParams: {
        caseSensitive: false,
        defaultOption: "startsWith",
      },
      floatingFilter: true,
    }),
    []
  );

  const fetchDailyWorkEntries = (d1, d2) => {
    if (!d1 || !d2 || !dayjs.isDayjs(d1) || !dayjs.isDayjs(d2)) {
      return Promise.resolve([]);
    }
    return APILABOURMGMT.DailyWorkEntries(
      d1.format("YYYY-MM-DD"),
      d2.format("YYYY-MM-DD")
    )
      .then((resp) => resp.json())
      .then((resp) => {
        if (resp.statusCode !== 200) {
          setAlertTitleText("Oops..", `An error occurred ${resp.message}`);
          return [];
        }
        const data = [...resp.response].sort((a, b) => b.id - a.id);
        const workOrders = getWorkOrders();
        data.forEach((x) => {
          const reqdWorkOrder = workOrders.filter(
            (y) => y.value === x.contractId
          );
          if (reqdWorkOrder.length > 0) {
            x.contractorId = reqdWorkOrder[0].dependentvalue;
            x.locationId = reqdWorkOrder[0].otherdependentvalue;
          }
        });
        return data;
      })
      .catch(() => {
        setAlertTitleText("Network Error", "Please try again.");
        return [];
      });
  };

  const onGridReady = async (params) => {
    if (date) setDateValue(dayjs(date));

    APIMASTER.SiteOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setSites(r.response);
      });

    APILABOURMGMT.WorkOrderOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode !== 200) return;
        setWorkOrders(r.response);
        const initFrom = date ? dayjs(date) : dateValue;
        const initTo = toDateValue;
        fetchDailyWorkEntries(initFrom, initTo).then((data) =>
          params.api.applyTransaction({ add: data })
        );
      });

    APIMASTER.SupplierCategories()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setSupplierCategories(r.response);
      });

    APIMASTER.SupplierOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setSuppliers(r.response);
      });

    APIMASTER.ProjectPhaseOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setProjectPhases(r.response);
      });

    APIMASTER.LabourerTypeOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setLabourerTypes(r.response);
      });

    APIMASTER.UomOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setUoms(r.response);
      });

    APIMASTER.UserOptions()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode === 200) setUsers(r.response);
      });

    APIMASTER.Items()
      .then((r) => r.json())
      .then((r) => {
        if (r.statusCode !== 200) return;
        const itemOptions = r.response.map((e) => ({
          name: e.itemName,
          value: e.id,
          dependentvalue: e.categoryId,
        }));
        setItems(itemOptions);
        setItemList(r.response);
      });

    if (filterModel) {
      params.api.setFilterModel(filterModel);
      params.api.onFilterChanged();
    }
    if (sortModel) {
      params.api.setSortModel(sortModel);
    }
  };

  const onFilterChanged = (e) => {
    const model = e.api.getFilterModel();
    dispatch(
      setDweFilterModel(model && Object.keys(model).length ? model : null)
    );
  };
  const onSortChanged = (e) => {
    const model = e.api.getSortModel();
    dispatch(setDweSortModel(model && model.length ? model : null));
  };

  return (
    <SideNav
      currentComponent={LABOUR_MANAGEMENT}
      subComponent={DAILY_WORK_ENTRY}
      subComponentPath={PATH_DAILY_WORK_ENTRY}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => {
            navigate(PATH_DAILY_WORK_ENTRY_DETAILS + 0, {
              state: { date: safeFrom, isEdit: false },
            });
          }}
        >
          {ADD}
        </Button>

        <Button
          onClick={deleteModel}
          variant="outlined"
          sx={{ borderColor: SPANISH_GREEN }}
          style={{
            textTransform: "capitalize",
            fontSize: 14,
            color: SPANISH_GREEN,
            minWidth: "180px",
            minHeight: "45px",
            marginRight: 10,
          }}
        >
          {DELETE}
        </Button>
      </div>

      <div
        style={{
          backgroundColor: LIGHT_GREY_BACKGROUND,
          borderRadius: "5px",
          minWidth: "90%",
          minHeight: "50px",
          padding: 20,
          marginBottom: 10,
          borderStyle: "solid",
          border: "1x",
          borderColor: "#eaeaea",
          display: "flex",
        }}
      >
        <div style={{ display: "flex" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="dateId"
              label="From Date"
              value={dateValue}
              onChange={(v) => setDateValue(v)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              id="toDateId"
              label="To Date"
              value={toDateValue}
              onChange={(v) => setToDateValue(v)}
              inputFormat="DD-MM-YYYY"
              renderInput={(params) => (
                <TextField {...params} style={{ marginLeft: 10 }} />
              )}
            />
          </LocalizationProvider>
        </div>
        <div align="right" style={{ width: "100%", right: 0 }}>
          <Button
            style={{
              backgroundColor: SPANISH_GREEN,
              textTransform: "capitalize",
              fontSize: 14,
              color: WHITE,
              minWidth: "129px",
              minHeight: "45px",
              marginTop: 5,
            }}
            onClick={() => {
              fetchDailyWorkEntries(dateValue, toDateValue).then((data) => {
                try {
                  const rows = [];
                  gridRef.current?.api?.forEachNode((n) => rows.push(n.data));
                  gridRef.current?.api?.applyTransaction({ remove: rows });
                } catch (e) {}
                try {
                  gridRef.current?.api?.applyTransaction({ add: data });
                } catch (e) {}
              });
            }}
          >
            {GO}
          </Button>
        </div>
      </div>

      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            copyGroupHeadersToClipboard
            enableRangeSelection
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            ref={gridRef}
            onGridReady={onGridReady}
            pagination
            paginationAutoPageSize
            rowSelection="multiple"
            getRowId={(p) => p.data.id}
            onSelectionChanged={onRowSelectionChanged}
            onFilterChanged={onFilterChanged}
            onSortChanged={onSortChanged}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>
    </SideNav>
  );
}
